import React from 'react';
import { Badge, BadgeType, Tooltip, ItemPagination } from '@enter/core';
import { ProviderEvidence } from '@enter/core';
import Icon from '@mdi/react';
import { mdiContentCopy, mdiShare, mdiUpdate, mdiCheckCircleOutline, mdiAlertCircleOutline } from '@mdi/js';
import { format } from 'date-fns';

interface EvidenceHeaderProps {
    evidence: ProviderEvidence;
    onCopyEvidenceId: () => void;
    onCopyEvidenceUrl: () => void;
    // Pagination props (optional - only shown when navigating from list)
    paginationOffset?: number;
    paginationTotal?: number;
    onPrevious?: () => void;
    onNext?: () => void;
    isNavigating?: boolean;
}

const getEvidenceStatusBadgeType = (status: string): BadgeType => {
    switch (status) {
        case "SUBMITTED":
            return BadgeType.ORANGE;
        case "APPROVED":
            return BadgeType.GREEN;
        case "DENIED":
            return BadgeType.RED;
        default:
            return BadgeType.BLUE_LIGHT;
    }
};

export const EvidenceHeader: React.FC<EvidenceHeaderProps> = ({
    evidence,
    onCopyEvidenceId,
    onCopyEvidenceUrl,
    paginationOffset,
    paginationTotal,
    onPrevious,
    onNext,
    isNavigating = false,
}) => {
    const hasPagination = paginationOffset !== undefined && paginationTotal !== undefined && onPrevious && onNext;

    return (
        <div>
            {/* Title and Action Buttons */}
            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <h1 style={{ margin: 0 }}>Evidence Details</h1>
                    
                    {hasPagination && (
                        <ItemPagination
                            currentIndex={paginationOffset}
                            total={paginationTotal}
                            onPrevious={onPrevious}
                            onNext={onNext}
                            isNavigating={isNavigating}
                            size="small"
                        />
                    )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {/* Copy Evidence ID Button */}
                    <Tooltip
                        direction="top"
                        variant="dark"
                        title={<span>Copy Evidence ID</span>}>
                        <button
                            style={{
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onClick={onCopyEvidenceId}
                        >
                            <Icon path={mdiContentCopy} size={0.8} />
                        </button>
                    </Tooltip>

                    {/* Copy Evidence URL Button */}
                    <Tooltip
                        direction="top"
                        variant="dark"
                        title={<span>Copy Evidence URL</span>}>
                        <button
                            style={{
                                background: 'transparent',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onClick={onCopyEvidenceUrl}
                        >
                            <Icon path={mdiShare} size={0.8} />
                        </button>
                    </Tooltip>
                </div>
            </div>

            {/* Status and Evidence Information as Badges */}
            <div style={{ 
                marginBottom: '20px',
                display: 'flex',
                gap: '10px',
                flexWrap: 'wrap',
                alignItems: 'center'
            }}>
                {/* Status Badge */}
                <Tooltip
                    direction="top"
                    variant="dark"
                    title={<span>Evidence Status: {evidence.status || 'N/A'}</span>}>
                    <Badge type={getEvidenceStatusBadgeType(evidence.status || '')} size="small">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {evidence.status === 'SUBMITTED' && (
                                <Icon path={mdiUpdate} size={0.7} />
                            )}
                            {evidence.status === 'APPROVED' && (
                                <Icon path={mdiCheckCircleOutline} size={0.7} />
                            )}
                            {evidence.status === 'DENIED' && (
                                <Icon path={mdiAlertCircleOutline} size={0.7} />
                            )}
                            <span>{evidence.status || 'N/A'}</span>
                        </span>
                    </Badge>
                </Tooltip>

                {/* Updated Badge */}
                {evidence.updated && (
                    <Tooltip
                        direction="top"
                        variant="dark"
                        title={<span>Last Updated</span>}>
                        <Badge type={BadgeType.BLUE_LIGHT} size="small">
                            Updated: {format(new Date(evidence.updated), 'MMM d, yyyy')}
                        </Badge>
                    </Tooltip>
                )}

                {/* Expires Badge */}
                {evidence.expired && (
                    <Tooltip
                        direction="top"
                        variant="dark"
                        title={<span>Expiration Date</span>}>
                        <Badge type={BadgeType.BLUE_LIGHT} size="small">
                            Expires: {format(new Date(evidence.expired), 'MMM d, yyyy')}
                        </Badge>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

